import { useEffect, useMemo, useState } from 'react';

const navItems = [
  { title: '至善足跡', url: '#foundation-footprints' },
  { title: '大事記', url: '#big-events' },
  { title: '影音專區', url: '#video-section' },
  { title: '校園友善風雲錄', url: '#awards-ceremony' },
  { title: '友愛日', url: '#love-day' },
];

function isActiveNavItem(itemUrl, currentPath, currentHash) {
  if (!itemUrl || !currentPath) {
    return false;
  }

  if (itemUrl === '/') {
    return currentPath === '/';
  }

  if (itemUrl.startsWith('/#')) {
    if (currentPath !== '/') {
      return false;
    }
    return currentHash === itemUrl.slice(1);
  }

  if (itemUrl.startsWith('#')) {
    if (currentPath !== '/') {
      return false;
    }
    return currentHash === itemUrl;
  }

  if (itemUrl.startsWith('http')) {
    return false;
  }

  return currentPath.startsWith(itemUrl);
}

function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [locationState, setLocationState] = useState({ path: '/', hash: '' });
  const sectionIds = useMemo(
    () =>
      navItems
        .map((item) => {
          if (item.url.startsWith('#')) {
            return item.url.slice(1);
          }
          if (item.url.startsWith('/#')) {
            return item.url.slice(2);
          }
          return null;
        })
        .filter(Boolean),
    []
  );

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const updateLocation = () => {
      setLocationState({ path: window.location.pathname, hash: window.location.hash });
    };

    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setMenuOpen(false);
      }
    };

    updateLocation();

    window.addEventListener('hashchange', updateLocation);
    window.addEventListener('popstate', updateLocation);
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('hashchange', updateLocation);
      window.removeEventListener('popstate', updateLocation);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    if (window.location.pathname !== '/') {
      return undefined;
    }

    if (!sectionIds.length) {
      return undefined;
    }

    const sections = sectionIds
      .map((id) => document.getElementById(id))
      .filter((section) => section);

    if (!sections.length) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const visibleEntries = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);

        let nextHash = '';

        if (visibleEntries.length) {
          nextHash = `#${visibleEntries[0].target.id}`;
        } else {
          const nearestSection = sections
            .map((section) => ({
              id: section.id,
              distance: Math.abs(section.getBoundingClientRect().top),
            }))
            .sort((a, b) => a.distance - b.distance)[0];
          if (nearestSection) {
            nextHash = `#${nearestSection.id}`;
          }
        }

        if (!nextHash) {
          return;
        }

        setLocationState((prev) => {
          if (prev.path === window.location.pathname && prev.hash === nextHash) {
            return prev;
          }
          return { path: window.location.pathname, hash: nextHash };
        });
      },
      {
        root: null,
        rootMargin: '-45% 0px -45% 0px',
        threshold: [0, 0.25, 0.5, 0.75, 1],
      }
    );

    sections.forEach((section) => observer.observe(section));

    return () => {
      sections.forEach((section) => observer.unobserve(section));
      observer.disconnect();
    };
  }, [sectionIds, locationState.path]);

  const toggleMenu = () => {
    setMenuOpen((prev) => !prev);
  };

  const handleNavClick = () => {
    setMenuOpen(false);
  };

  return (
    <header className="site-header">
      <nav className="site-nav">
        <div className="site-nav__bar">
          <a className="site-nav__logo" href="/" title="至善30">
            <img
              src="./assets/foundation-header-wordmark.png"
              alt="至善30"
              width="250"
              height="40"
              loading="eager"
            />
          </a>
          <button
            type="button"
            className="site-nav__toggle"
            aria-expanded={menuOpen}
            aria-controls="mobile-menu"
            aria-label="切換導覽選單"
            onClick={toggleMenu}
          >
            <svg className="site-nav__toggle-icon" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path className="hamburger-line" strokeLinecap="round" strokeLinejoin="round" d="M4 6h16" />
              <path className="hamburger-line" strokeLinecap="round" strokeLinejoin="round" d="M4 12h16" />
              <path className="hamburger-line" strokeLinecap="round" strokeLinejoin="round" d="M4 18h16" />
              <path className="close-line" strokeLinecap="round" strokeLinejoin="round" d="M18 6l-12 12" />
              <path className="close-line" strokeLinecap="round" strokeLinejoin="round" d="M6 6l12 12" />
            </svg>
          </button>
          <div className="site-nav__links">
            {navItems.map((item) => {
              const active = isActiveNavItem(item.url, locationState.path, locationState.hash);
              return (
                <a
                  key={item.title}
                  href={item.url}
                  className={`site-nav__link${active ? ' is-active' : ''}`}
                  target={item.url.startsWith('http') ? '_blank' : undefined}
                  rel={item.url.startsWith('http') ? 'noreferrer noopener' : undefined}
                  onClick={item.url.startsWith('http') ? undefined : handleNavClick}
                >
                  {item.title}
                </a>
              );
            })}
          </div>
        </div>
        <div id="mobile-menu" className={`site-nav__mobile${menuOpen ? ' is-open' : ''}`}>
          {navItems.map((item) => {
            const active = isActiveNavItem(item.url, locationState.path, locationState.hash);
            return (
              <a
                key={`mobile-${item.title}`}
                href={item.url}
                className={`site-nav__mobile-link${active ? ' is-active' : ''}`}
                target={item.url.startsWith('http') ? '_blank' : undefined}
                rel={item.url.startsWith('http') ? 'noreferrer noopener' : undefined}
                onClick={handleNavClick}
              >
                {item.title}
              </a>
            );
          })}
        </div>
      </nav>
    </header>
  );
}

export default Header;
